/**
 * EditWorkshopModal — MED-564 (E18-8)
 * Edit a workshop definition: title, description, date/time, duration, meeting link, facilitator.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Switch,
  Alert,
} from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertTriangle } from 'react-feather';
import { DateTimeField } from '../../../../components/common';
import {
  DEFAULT_START_TIME_ON_DATE_PICK,
  defaultSessionLengthMinutes,
  endFieldDefaults,
  endTimeError,
  nextEndTime,
} from '../../../../utils/sessionEndTime';
import { workshopsApi, type WorkshopWithSession } from '../../../../services/api/workshops';
import { cohortCoachesApi } from '../../../../services/api/cohortCoaches';

interface EditWorkshopModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  workshop: WorkshopWithSession | null;
  onSaved: () => void;
}

export function EditWorkshopModal({
  opened,
  onClose,
  cohortId,
  workshop,
  onSaved,
}: EditWorkshopModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledStart, setScheduledStart] = useState<Date | null>(null);
  const [scheduledEnd, setScheduledEnd] = useState<Date | null>(null);
  const [durationMinutes, setDurationMinutes] = useState<number | string>('');
  const [zoomLink, setZoomLink] = useState('');
  const [facilitatorId, setFacilitatorId] = useState<string | null>(null);
  const [trackAttendance, setTrackAttendance] = useState(true);
  const [coachOptions, setCoachOptions] = useState<ComboboxItem[]>([]);
  const [saving, setSaving] = useState(false);

  // Populate form from workshop data when modal opens
  useEffect(() => {
    if (!opened || !workshop) return;
    setTitle(workshop.title);
    setDescription(workshop.description || '');
    const start = workshop.scheduled_start ? new Date(workshop.scheduled_start) : null;
    setScheduledStart(start);
    // MED-1350: a dated workshop with no end shows the end that Save would
    // derive from its length, so the field never opens empty.
    setScheduledEnd(
      workshop.scheduled_end
        ? new Date(workshop.scheduled_end)
        : nextEndTime({
            previousStart: null,
            previousEnd: null,
            nextStart: start,
            defaultLengthMinutes: workshop.session_duration_minutes,
          }),
    );
    setDurationMinutes(workshop.session_duration_minutes ?? '');
    setZoomLink(workshop.zoom_link || '');
    setFacilitatorId(workshop.facilitator_id);
    setTrackAttendance(workshop.attendance_tracking_enabled ?? true);
  }, [opened, workshop]);

  // Load assigned coaches for facilitator select
  useEffect(() => {
    if (!opened) return;
    cohortCoachesApi
      .list(cohortId, { limit: 100 })
      .then(({ data }) => {
        setCoachOptions(
          data.map((c) => ({
            value: c.user_id,
            label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email,
          })),
        );
      })
      .catch(() => {
        // Non-blocking — facilitator select will just be empty
      });
  }, [opened, cohortId]);

  // MED-1350: the shared end time rule (utils/sessionEndTime.ts). The length
  // is the Duration field, which holds the workshop definition's length.
  const handleStartChange = (nextStart: Date | null) => {
    setScheduledStart(nextStart);
    setScheduledEnd(
      nextEndTime({
        previousStart: scheduledStart,
        previousEnd: scheduledEnd,
        nextStart,
        defaultLengthMinutes: defaultSessionLengthMinutes(
          { session_type: 'workshop_session' },
          { workshopLengthMinutes: durationMinutes === '' ? null : Number(durationMinutes) },
        ),
      }),
    );
  };

  // MED-1350: an end at or before the start, or a start with no end, blocks Save.
  const endError = endTimeError(scheduledStart, scheduledEnd);

  // MED-1360: an empty End field opens on the start's month and shows a time.
  // A date-only pick gives the start plus the Duration. A typed end is kept
  // as typed; endError reports one at or before the start.
  const workshopLengthMinutes = durationMinutes === '' ? null : Number(durationMinutes);
  const endDefaults = endFieldDefaults(scheduledStart, workshopLengthMinutes);

  const handleSave = async () => {
    if (!workshop) return;
    if (endError) return;

    setSaving(true);
    try {
      await workshopsApi.update(cohortId, workshop.id, {
        title: title.trim() || workshop.title,
        description: description || null,
        facilitator_id: facilitatorId,
        session_duration_minutes: durationMinutes !== '' ? Number(durationMinutes) : null,
        scheduled_start: scheduledStart ? scheduledStart.toISOString() : null,
        scheduled_end: scheduledEnd ? scheduledEnd.toISOString() : null,
        zoom_link: zoomLink.trim() || null,
        attendance_tracking_enabled: trackAttendance,
      });

      notifications.show({ message: 'Workshop updated', color: 'green' });
      onSaved();
      onClose();
    } catch {
      notifications.show({ message: 'Failed to update workshop', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  if (!workshop) return null;

  const isEdit = !!workshop.scheduled_start;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fw={700} size="lg">
            {isEdit ? 'Edit Workshop' : 'Configure Workshop'}
          </Text>
          <Text size="sm" c="dimmed">
            Set up the details for this workshop session
          </Text>
        </Stack>
      }
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Title"
          placeholder="Workshop title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          required
        />

        <Textarea
          label="Description"
          placeholder="Describe what participants will learn or do in this workshop"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={3}
          maxRows={6}
        />

        <DateTimeField
          label="Start Date & Time"
          placeholder="Select start date and time"
          value={scheduledStart}
          onChange={handleStartChange}
          defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
        />

        <DateTimeField
          label="End Date & Time"
          placeholder="Select end date and time"
          value={scheduledEnd}
          onChange={setScheduledEnd}
          minDate={scheduledStart || undefined}
          defaultDate={endDefaults.defaultDate}
          defaultTimeOnDatePick={endDefaults.defaultTimeOnDatePick}
          valueOnFirstDatePick={endDefaults.endOnDatePick}
          error={endError}
        />

        <NumberInput
          label="Duration (minutes)"
          placeholder="e.g. 120"
          value={durationMinutes}
          onChange={setDurationMinutes}
          min={15}
          max={480}
          step={15}
          // MED-1360: the help text follows nextEndTime in utils/sessionEndTime.ts.
          description="When you change the start time, the end time moves with it. If the start and end were not a valid pair, the end becomes the start plus this length."
        />

        <TextInput
          label="Meeting Link"
          placeholder="https://zoom.us/j/..."
          value={zoomLink}
          onChange={(e) => setZoomLink(e.currentTarget.value)}
        />

        <Select
          label="Facilitator"
          placeholder={coachOptions.length === 0 ? 'No coaches assigned to cohort' : 'Select a coach'}
          data={coachOptions}
          value={facilitatorId}
          onChange={setFacilitatorId}
          clearable
          searchable
          disabled={coachOptions.length === 0}
        />

        {/* MED-969 AC10b: Warn when facilitator is being removed */}
        {workshop?.facilitator_id && facilitatorId !== workshop.facilitator_id && (
          <Alert icon={<AlertTriangle size={16} />} color="orange" variant="light">
            <Text size="sm">
              Removing this facilitator also removes them from the workshop participant list.
              If they should stay on as a regular participant, re-add them via
              &ldquo;+ Add Participants&rdquo; after saving.
            </Text>
          </Alert>
        )}

        <Switch
          label="Track Attendance"
          description="When enabled, the facilitator will be prompted to submit attendance after the workshop and reminder nudges will fire if it is not submitted."
          checked={trackAttendance}
          onChange={(e) => setTrackAttendance(e.currentTarget.checked)}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!!endError}
            color="medleyGreen"
          >
            Save Workshop
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditWorkshopModal;
